// Raising a support ticket from the Admin app on a clinic's behalf (work item 27050) - see
// SupportTicketController.AdminCreate and CompanyController.GetClinicOptions in the API.

/** A clinic in the Add Ticket dialog's picker. */
export interface ClinicOption {
  Id: number;
  CompanyName: string | null;
  DoctorName: string | null;
  Email: string | null;
  Domain: string | null;
}

export interface AdminCreateTicketPayload {
  companyId: number;
  subject: string;
  description: string;
  priority: number;
  attachmentImages: File[];
}

/** The same cap as the clinic app's Raise a ticket form. */
export const MAX_TICKET_ATTACHMENTS = 3;

/** SupportTicket.Subject is NVARCHAR(300). */
export const MAX_TICKET_SUBJECT_LENGTH = 300;

/** How many matching clinics the picker lists at once - typing narrows them down. */
const CLINIC_OPTION_LIMIT = 50;

/** How a clinic is named in the picker: its clinic name, else its doctor, email or id. */
export function clinicLabel(clinic: ClinicOption | null | undefined): string {
  if (!clinic) return '';
  return clinic.CompanyName?.trim() || clinic.DoctorName?.trim() || clinic.Email?.trim() || `Clinic #${clinic.Id}`;
}

/** The clinics whose name, doctor, email or domain contains `search`, at most `limit` of them. */
export function filterClinicOptions(clinics: ClinicOption[], search: string, limit = CLINIC_OPTION_LIMIT): ClinicOption[] {
  const term = search.trim().toLowerCase();
  const matches = term
    ? clinics.filter(clinic => [clinic.CompanyName, clinic.DoctorName, clinic.Email, clinic.Domain]
      .some(value => value?.toLowerCase().includes(term)))
    : clinics;
  return matches.slice(0, limit);
}

/** Adds the picked images to `current`, skipping non-images and anything past MAX_TICKET_ATTACHMENTS. */
export function addTicketAttachments(current: File[], picked: File[]): { files: File[]; skipped: number } {
  const room = Math.max(MAX_TICKET_ATTACHMENTS - current.length, 0);
  const added = picked.filter(file => file.type.startsWith('image/')).slice(0, room);
  return { files: [...current, ...added], skipped: picked.length - added.length };
}

/**
 * SupportTicket/AdminCreate is multipart/form-data: the DTO's PascalCase fields, plus the images under the
 * controller's own `attachmentImages` parameter name.
 */
export function buildAdminCreateTicketFormData(payload: AdminCreateTicketPayload): FormData {
  const formData = new FormData();
  formData.append('CompanyId', String(payload.companyId));
  formData.append('Subject', payload.subject.trim());
  formData.append('Description', payload.description.trim());
  formData.append('Priority', String(payload.priority));
  for (const file of payload.attachmentImages) {
    formData.append('attachmentImages', file);
  }
  return formData;
}
